import { useCallback, useEffect, useRef, useState } from 'react'
import {
  addConfirmedHeadingArrow,
  removeHeadingArrowsFromMarker,
  showHeadingSelectionArrows,
} from '../HeadingArrow'

const UNVALIDATED_COLOR = '#E87722'
const VALIDATED_COLOR = '#0532ff'

export type HeadingSelectionState = {
  /** ID of the waypoint currently awaiting heading selection, or null. */
  pendingHeadingWaypointId: string | null
  /** Map of waypointId → confirmed heading degrees (0–359). */
  headingDegrees: Record<string, number>
  /** Whether the pending waypoint fell back to cardinal directions. */
  isCardinalFallback: boolean
  headingToastVisible: boolean

  startHeadingSelection: (
    waypointId: string,
    markerEl: HTMLElement,
    availableDirections: number[],
    isCardinalFallback: boolean,
    isValidated: boolean,
    existingHeading: number | null,
  ) => void
  /**
   * Called on marker drag start: removes any existing heading and marks the
   * waypoint as pending WITHOUT rendering heading-selection arrow buttons.
   * The drag handler in RouteMap will call showHeadingSelectionArrows during
   * movement via the pendingHeadingWaypointId prop, without interrupting drag.
   */
  prepareDragHeadingSelection: (waypointId: string) => void
  confirmHeading: (
    waypointId: string,
    bearing: number,
    markerEl: HTMLElement | null,
    isValidated: boolean,
  ) => void
  removeHeading: (waypointId: string, markerEl: HTMLElement | null) => void
  cancelHeadingSelection: (markerEl: HTMLElement | null) => void
  /**
   * Merge externally-sourced headings (e.g. pasted waypoints) into the map.
   * headingDegrees — not the waypoint.heading field — is what the map, the
   * copy serializer, and submit all read, so a pasted heading is invisible
   * until it lands here.
   */
  setHeadings: (headings: Record<string, number>) => void
}

/**
 * Manages the full heading-selection interaction for route waypoints.
 * Exposes state + actions; the calling component threads marker elements in.
 */
export const useHeadingSelection = (
  initialHeadings: Record<string, number> = {},
): HeadingSelectionState => {
  const [pendingHeadingWaypointId, setPendingHeadingWaypointId] = useState<
    string | null
  >(null)
  const [headingDegrees, setHeadingDegrees] =
    useState<Record<string, number>>(initialHeadings)
  const [isCardinalFallback, setIsCardinalFallback] = useState(false)
  const [headingToastVisible, setHeadingToastVisible] = useState(false)

  // Keep a ref to the pending marker element for keyboard events
  const pendingMarkerElRef = useRef<HTMLElement | null>(null)
  const pendingWaypointIdRef = useRef<string | null>(null)

  const confirmHeading = useCallback(
    (
      waypointId: string,
      bearing: number,
      markerEl: HTMLElement | null,
      isValidated: boolean,
    ) => {
      setHeadingDegrees(prev => ({ ...prev, [waypointId]: bearing }))
      setPendingHeadingWaypointId(null)
      setHeadingToastVisible(false)
      pendingMarkerElRef.current = null
      pendingWaypointIdRef.current = null

      if (markerEl) {
        const strokeColor = isValidated ? VALIDATED_COLOR : UNVALIDATED_COLOR
        addConfirmedHeadingArrow(markerEl, bearing, strokeColor)
      }
    },
    [],
  )

  const removeHeading = useCallback(
    (waypointId: string, markerEl: HTMLElement | null) => {
      setHeadingDegrees(prev => {
        const next = { ...prev }
        delete next[waypointId]
        return next
      })
      if (markerEl) removeHeadingArrowsFromMarker(markerEl)
    },
    [],
  )

  const startHeadingSelection = useCallback(
    (
      waypointId: string,
      markerEl: HTMLElement,
      availableDirections: number[],
      cardinalFallback: boolean,
      isValidated: boolean,
      existingHeading: number | null,
    ) => {
      setPendingHeadingWaypointId(waypointId)
      setIsCardinalFallback(cardinalFallback)
      setHeadingToastVisible(true)
      pendingMarkerElRef.current = markerEl
      pendingWaypointIdRef.current = waypointId

      const strokeColor = isValidated ? VALIDATED_COLOR : UNVALIDATED_COLOR

      showHeadingSelectionArrows(markerEl, {
        strokeColor,
        availableDirections,
        onHeadingSelect: (bearing: number) => {
          confirmHeading(waypointId, bearing, markerEl, isValidated)
        },
        hasExistingHeading: existingHeading !== null,
        onRemoveHeading: () => {
          removeHeading(waypointId, markerEl)
          setPendingHeadingWaypointId(null)
          setHeadingToastVisible(false)
          pendingMarkerElRef.current = null
          pendingWaypointIdRef.current = null
        },
      })
    },
    [confirmHeading, removeHeading],
  )

  const cancelHeadingSelection = useCallback((markerEl: HTMLElement | null) => {
    if (markerEl) removeHeadingArrowsFromMarker(markerEl)
    setPendingHeadingWaypointId(null)
    setHeadingToastVisible(false)
    pendingMarkerElRef.current = null
    pendingWaypointIdRef.current = null
  }, [])

  const setHeadings = useCallback((headings: Record<string, number>) => {
    setHeadingDegrees(prev => ({ ...prev, ...headings }))
  }, [])

  const prepareDragHeadingSelection = useCallback((waypointId: string) => {
    // Remove any existing heading for this waypoint
    setHeadingDegrees(prev => {
      if (!(waypointId in prev)) return prev
      const next = { ...prev }
      delete next[waypointId]
      return next
    })
    // Mark as pending — called from dragend so it's safe to show the toast.
    setPendingHeadingWaypointId(waypointId)
    setHeadingToastVisible(true)
    pendingWaypointIdRef.current = waypointId
    // Do NOT call showHeadingSelectionArrows here — that would add DOM buttons
    // which intercept pointer events and interrupt the drag gesture.
  }, [])

  // Keyboard handler: Arrow keys rotate pending heading, Enter confirms, Escape cancels
  useEffect(() => {
    const handle = (e: KeyboardEvent) => {
      const waypointId = pendingWaypointIdRef.current
      const markerEl = pendingMarkerElRef.current
      if (!waypointId) return

      if (e.key === 'Escape') {
        e.preventDefault()
        cancelHeadingSelection(markerEl)
        return
      }

      if (e.key === 'Enter') {
        e.preventDefault()
        const current = headingDegrees[waypointId]
        if (current !== undefined) {
          confirmHeading(waypointId, current, markerEl, false)
        }
        return
      }

      if (e.key === 'ArrowLeft' || e.key === 'ArrowRight') {
        e.preventDefault()
        setHeadingDegrees(prev => {
          const current = prev[waypointId] ?? 0
          const delta = e.key === 'ArrowLeft' ? -15 : 15
          return {
            ...prev,
            [waypointId]: (((current + delta) % 360) + 360) % 360,
          }
        })
      }
    }

    window.addEventListener('keydown', handle)
    return () => window.removeEventListener('keydown', handle)
  }, [headingDegrees, confirmHeading, cancelHeadingSelection])

  return {
    pendingHeadingWaypointId,
    headingDegrees,
    isCardinalFallback,
    headingToastVisible,
    startHeadingSelection,
    prepareDragHeadingSelection,
    confirmHeading,
    removeHeading,
    cancelHeadingSelection,
    setHeadings,
  }
}
